import type { SpinSlotResponse } from '@goodtime/shared';

/**
 * When the game offers the optional account (producer 2026-10-14): at good
 * moments (a big win, a level up, a new world, a chapter done), never on a
 * first visit, at most once a launch and three days apart, four times in
 * all, and not again after "Not now" three times.
 */

export type AccountMoment = 'big-win' | 'level-up' | 'new-world' | 'chapter';

export interface OfferRecord {
  shown: number;
  dismissed: number;
  /** Epoch ms of the last offer; 0 for never. */
  lastShownAt: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;
export const OFFER_GAP_MS = 3 * DAY_MS;
export const MAX_OFFERS = 4;
export const MAX_DISMISSALS = 3;
/** A win this many times the bet or more is a big one. */
export const BIG_WIN_TIMES_BET = 10;

const KEY = 'goodtime.accountOffers';
const NONE: OfferRecord = { shown: 0, dismissed: 0, lastShownAt: 0 };

/** Whether the record allows an offer now. */
export function offerAllowed(record: OfferRecord, now: number): boolean {
  if (record.shown >= MAX_OFFERS || record.dismissed >= MAX_DISMISSALS) return false;
  return now - record.lastShownAt >= OFFER_GAP_MS;
}

export function recordShown(record: OfferRecord, now: number): OfferRecord {
  return { ...record, shown: record.shown + 1, lastShownAt: now };
}

export function recordDismissed(record: OfferRecord): OfferRecord {
  return { ...record, dismissed: record.dismissed + 1 };
}

/** The good moment a spin made, if any: a big win first, then a level up. */
export function spinMoment(response: SpinSlotResponse): AccountMoment | null {
  const { totalWinnings, bet } = response.settlement;
  if (bet > 0 && totalWinnings >= bet * BIG_WIN_TIMES_BET) return 'big-win';
  if (response.levelUp) return 'level-up';
  return null;
}

export function readOfferRecord(): OfferRecord {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return NONE;
    const v = JSON.parse(raw) as Partial<OfferRecord>;
    return {
      shown: Number(v.shown) || 0,
      dismissed: Number(v.dismissed) || 0,
      lastShownAt: Number(v.lastShownAt) || 0,
    };
  } catch {
    return NONE;
  }
}

export function writeOfferRecord(record: OfferRecord): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(record));
  } catch {
    /* private mode: offers just aren't remembered */
  }
}
